<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import AiAssistSettingsAPI from 'dashboard/api/aiAssist/settings';

const { t } = useI18n();
const captainConfigStore = useCaptainConfigStore();

const DEFAULT_PERSONA =
  'You will be a seasoned customer support agent. You will consistently verify the accuracy of my responses before sending.';

const isLoading = ref(true);
const isSaving = ref(false);
const persona = ref('');
const replyOutsideKnowledge = ref(false);
const useSnippets = ref(true);

// ── Knowledge Sources ──────────────────────────────────────────────────────
const knowledgeSources = ref([]);
const showAddForm = ref(false);
const newSourceType = ref('website_url');
const newSourceName = ref('');
const newSourceUrl = ref('');
const newSourceFile = ref(null);
const isSavingSource = ref(false);

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await AiAssistSettingsAPI.get();
    persona.value = data.persona || DEFAULT_PERSONA;
    replyOutsideKnowledge.value = data.reply_outside_knowledge ?? false;
    useSnippets.value = data.use_snippets ?? true;
  } catch {
    useAlert(t('AI_ASSIST_SETTINGS.SAVE_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const saveToggle = async () => {
  try {
    await AiAssistSettingsAPI.update({
      reply_outside_knowledge: replyOutsideKnowledge.value,
      use_snippets: useSnippets.value,
    });
  } catch {
    useAlert(t('AI_ASSIST_SETTINGS.SAVE_ERROR'));
  }
};

const savePersona = async () => {
  isSaving.value = true;
  try {
    await AiAssistSettingsAPI.update({ persona: persona.value });
    useAlert(t('AI_ASSIST_SETTINGS.SAVE_SUCCESS'));
  } catch {
    useAlert(t('AI_ASSIST_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const resetPersona = () => {
  persona.value = DEFAULT_PERSONA;
};

const handleFileChange = e => {
  newSourceFile.value = e.target.files[0] || null;
};

const cancelAddSource = () => {
  showAddForm.value = false;
  newSourceName.value = '';
  newSourceUrl.value = '';
  newSourceFile.value = null;
  newSourceType.value = 'website_url';
};

const addKnowledgeSource = async () => {
  if (!newSourceName.value.trim()) return;
  isSavingSource.value = true;
  try {
    const source = {
      id: Date.now(),
      name: newSourceName.value.trim(),
      type: newSourceType.value,
      url: newSourceUrl.value.trim() || null,
    };
    knowledgeSources.value.push(source);
    useAlert(t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.ADD_SUCCESS'));
    cancelAddSource();
  } catch {
    useAlert(t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.SAVE_ERROR'));
  } finally {
    isSavingSource.value = false;
  }
};

const removeKnowledgeSource = id => {
  knowledgeSources.value = knowledgeSources.value.filter(s => s.id !== id);
  useAlert(t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.DELETE_SUCCESS'));
};

onMounted(async () => {
  fetchSettings();
  if (!Object.keys(captainConfigStore.getFeatures).length) {
    await captainConfigStore.fetch();
  }
});
</script>

<template>
  <div class="flex w-full max-w-none min-w-0 flex-col gap-6">
    <BaseSettingsHeader
      :title="t('AI_ASSIST_SETTINGS.TITLE')"
      :description="t('AI_ASSIST_SETTINGS.DESCRIPTION')"
    >
      <template #actions>
        <Button
          size="sm"
          variant="faded"
          icon="i-lucide-flask-conical"
          :label="t('AI_ASSIST_SETTINGS.TEST_BUTTON')"
        />
        <Button
          size="sm"
          icon="i-lucide-database"
          :label="t('AI_ASSIST_SETTINGS.KNOWLEDGE_BUTTON')"
          @click="
            document.getElementById('ai-assist-knowledge').scrollIntoView({
              behavior: 'smooth',
            })
          "
        />
      </template>
    </BaseSettingsHeader>

    <div class="flex w-full min-w-0 flex-col gap-4 px-8 md:px-10">
      <div v-if="isLoading" class="flex justify-center py-12">
        <SpinnerLoader :size="32" class="text-n-blue-9" />
      </div>

      <template v-else>
        <!-- Toggles -->
        <div
          class="rounded-lg border border-n-weak bg-n-solid-1 divide-y divide-n-weak dark:border-n-slate-6 dark:divide-n-slate-6"
        >
          <div class="flex items-center justify-between px-5 py-4 gap-4">
            <div class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('AI_ASSIST_SETTINGS.TOGGLES.REPLY_OUTSIDE_KNOWLEDGE') }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{
                  t('AI_ASSIST_SETTINGS.TOGGLES.REPLY_OUTSIDE_KNOWLEDGE_DESC')
                }}
              </span>
            </div>
            <Switch v-model="replyOutsideKnowledge" @change="saveToggle" />
          </div>

          <div class="flex items-center justify-between px-5 py-4 gap-4">
            <div class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('AI_ASSIST_SETTINGS.TOGGLES.USE_SNIPPETS') }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ t('AI_ASSIST_SETTINGS.TOGGLES.USE_SNIPPETS_DESC') }}
              </span>
            </div>
            <Switch v-model="useSnippets" @change="saveToggle" />
          </div>
        </div>

        <!-- Persona -->
        <div
          class="rounded-lg border border-n-weak bg-n-solid-1 p-5 flex flex-col gap-3 dark:border-n-slate-6"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('AI_ASSIST_SETTINGS.PERSONA.TITLE') }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ t('AI_ASSIST_SETTINGS.PERSONA.DESCRIPTION') }}
              </span>
            </div>
            <Button
              size="sm"
              variant="faded"
              icon="i-lucide-rotate-ccw"
              :label="t('AI_ASSIST_SETTINGS.PERSONA.RESET')"
              @click="resetPersona"
            />
          </div>
          <textarea
            v-model="persona"
            rows="4"
            class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand resize-none dark:border-n-slate-6"
            :placeholder="t('AI_ASSIST_SETTINGS.PERSONA.PLACEHOLDER')"
          />
          <div class="flex justify-end">
            <Button
              size="sm"
              :label="t('AI_ASSIST_SETTINGS.PERSONA.SAVE')"
              :disabled="isSaving"
              @click="savePersona"
            />
          </div>
        </div>

        <!-- Knowledge Sources -->
        <div
          id="ai-assist-knowledge"
          class="rounded-lg border border-n-weak bg-n-solid-1 dark:border-n-slate-6 overflow-hidden"
        >
          <div
            class="px-5 py-4 border-b border-n-weak dark:border-n-slate-6 flex items-center justify-between"
          >
            <div>
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.TITLE') }}
              </h2>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.DESCRIPTION') }}
              </p>
            </div>
            <Button
              v-if="!showAddForm"
              size="sm"
              icon="i-lucide-plus"
              :label="t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.ADD_BUTTON')"
              @click="showAddForm = true"
            />
          </div>

          <!-- Add form -->
          <div
            v-if="showAddForm"
            class="px-5 py-4 border-b border-n-weak dark:border-n-slate-6 bg-n-alpha-1 flex flex-col gap-3"
          >
            <div class="flex gap-3">
              <button
                v-for="opt in ['website_url', 'file']"
                :key="opt"
                class="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors"
                :class="
                  newSourceType === opt
                    ? 'border-n-brand bg-n-brand/10 text-n-brand'
                    : 'border-n-weak text-n-slate-11 hover:text-n-slate-12'
                "
                @click="newSourceType = opt"
              >
                <span
                  :class="
                    opt === 'website_url'
                      ? 'i-lucide-globe size-3.5'
                      : 'i-lucide-upload size-3.5'
                  "
                />
                {{
                  opt === 'website_url'
                    ? t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.TYPE_URL')
                    : t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.TYPE_FILE')
                }}
              </button>
            </div>
            <input
              v-model="newSourceName"
              class="w-full rounded-md border border-n-weak bg-n-solid-2 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand dark:border-n-slate-6"
              :placeholder="
                t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.NAME_LABEL')
              "
            />
            <input
              v-if="newSourceType === 'website_url'"
              v-model="newSourceUrl"
              type="url"
              class="w-full rounded-md border border-n-weak bg-n-solid-2 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand dark:border-n-slate-6"
              :placeholder="
                t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.URL_PLACEHOLDER')
              "
            />
            <div
              v-else
              class="rounded-md border-2 border-dashed border-n-weak flex items-center justify-center h-24 text-sm text-n-slate-11 cursor-pointer hover:border-n-brand dark:border-n-slate-6"
              @click="$refs.fileInput.click()"
            >
              <span class="i-lucide-upload-cloud mr-2 size-4" />
              {{ t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.FILE_PLACEHOLDER') }}
            </div>
            <input
              ref="fileInput"
              type="file"
              class="hidden"
              accept=".pdf,.docx,.txt"
              @change="handleFileChange"
            />
            <div class="flex gap-2 justify-end">
              <Button
                size="sm"
                variant="faded"
                :label="t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.CANCEL')"
                @click="cancelAddSource"
              />
              <Button
                size="sm"
                :label="t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.SAVE_BUTTON')"
                :disabled="isSavingSource || !newSourceName.trim()"
                @click="addKnowledgeSource"
              />
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-if="knowledgeSources.length === 0 && !showAddForm"
            class="flex flex-col items-center justify-center py-10 px-6 text-center gap-2"
          >
            <span class="i-lucide-database size-8 text-n-slate-9" />
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.EMPTY') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ t('AI_ASSIST_SETTINGS.KNOWLEDGE_SOURCES.EMPTY_DESC') }}
            </p>
          </div>

          <!-- Sources list -->
          <div
            v-else-if="knowledgeSources.length > 0"
            class="divide-y divide-n-weak dark:divide-n-slate-6"
          >
            <div
              v-for="source in knowledgeSources"
              :key="source.id"
              class="flex items-center justify-between px-5 py-3 gap-3 hover:bg-n-alpha-1"
            >
              <div class="flex items-center gap-3 min-w-0">
                <span
                  :class="
                    source.type === 'website_url'
                      ? 'i-lucide-globe size-4 text-n-slate-9 shrink-0'
                      : 'i-lucide-file-text size-4 text-n-slate-9 shrink-0'
                  "
                />
                <div class="min-w-0">
                  <p class="text-sm font-medium text-n-slate-12 truncate">
                    {{ source.name }}
                  </p>
                  <p v-if="source.url" class="text-xs text-n-slate-11 truncate">
                    {{ source.url }}
                  </p>
                </div>
              </div>
              <button
                class="shrink-0 text-n-slate-9 hover:text-ruby-9 transition-colors"
                @click="removeKnowledgeSource(source.id)"
              >
                <span class="i-lucide-trash-2 size-4" />
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
